import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Plus, Trash2, Power } from "lucide-react";
import { api, type Shop } from "@/lib/api";
import { EmptyState, ErrorState } from "@/components/empty-state";
import { ShopLogo } from "@/components/shop-logo";

export const Route = createFileRoute("/admin/shops")({
  component: AdminShops,
});

function AdminShops() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-shops"], queryFn: () => api.adminShops() });
  const [open, setOpen] = useState(false);

  const create = useMutation({
    mutationFn: (body: Partial<Shop>) => api.adminCreateShop(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-shops"] }); setOpen(false); },
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Shop> }) => api.adminUpdateShop(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-shops"] }),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.adminDeleteShop(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-shops"] }),
  });

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">Shops</h1>
          <p className="text-sm text-muted-foreground">Shops are added manually. No auto-imported sellers.</p>
        </div>
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-3 py-2 text-sm font-semibold text-primary-foreground">
          <Plus className="h-4 w-4" /> Add shop
        </button>
      </header>

      {q.isError && <ErrorState message={(q.error as Error).message} />}
      {q.isSuccess && q.data.data.length === 0 && !open && (
        <EmptyState title="No shops yet" description="Click 'Add shop' to create your first shop." action={<button onClick={() => setOpen(true)} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">Add shop</button>} />
      )}

      {open && <ShopForm onCancel={() => setOpen(false)} onSubmit={(b) => create.mutate(b)} submitting={create.isPending} error={create.error as Error | null} />}

      {q.isSuccess && q.data.data.length > 0 && (
        <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Website</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {q.data.data.map((s) => (
                <tr key={s.id} className="border-t border-border/60">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <ShopLogo shop={s} size="xs" />
                      {s.name}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-muted-foreground">{s.slug}</td>
                  <td className="px-4 py-2 text-muted-foreground"><a href={s.websiteUrl ?? "#"} target="_blank" rel="noreferrer" className="hover:text-primary">{s.websiteUrl ?? "—"}</a></td>
                  <td className="px-4 py-2">{s.isActive ? <span className="text-success">yes</span> : <span className="text-muted-foreground">no</span>}</td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => update.mutate({ id: s.id, body: { isActive: !s.isActive } })} className="mr-2 inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs hover:border-primary">
                      <Power className="h-3 w-3" /> Toggle
                    </button>
                    <button onClick={() => confirm(`Delete shop "${s.name}"?`) && del.mutate(s.id)} className="inline-flex items-center gap-1 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive hover:bg-destructive/20">
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ShopForm({ onCancel, onSubmit, submitting, error }: { onCancel: () => void; onSubmit: (b: Partial<Shop>) => void; submitting: boolean; error: Error | null }) {
  const [form, setForm] = useState<Partial<Shop>>({ name: "", websiteUrl: "", logoUrl: "", isActive: true });
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}
      className="rounded-xl border border-border bg-card p-5"
    >
      <h2 className="mb-4 font-display text-lg font-semibold">New shop</h2>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Name *"><input required value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
        <Field label="Slug (optional)"><input value={(form as any).slug ?? ""} onChange={(e) => setForm({ ...form, slug: e.target.value } as any)} className={inputCls} /></Field>
        <Field label="Website URL"><input type="url" value={form.websiteUrl ?? ""} onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })} className={inputCls} /></Field>
        <Field label="Logo path / URL"><input value={form.logoUrl ?? ""} onChange={(e) => setForm({ ...form, logoUrl: e.target.value })} className={inputCls} /></Field>
        <Field label="Affiliate base URL (optional)"><input value={(form as any).affiliateBaseUrl ?? ""} onChange={(e) => setForm({ ...form, affiliateBaseUrl: e.target.value } as any)} className={inputCls} /></Field>
        <Field label="Trust score (0–5)"><input type="number" step="0.1" min={0} max={5} value={(form as any).trustScore ?? ""} onChange={(e) => setForm({ ...form, trustScore: e.target.value ? Number(e.target.value) : null } as any)} className={inputCls} /></Field>
      </div>
      {error && <p className="mt-3 rounded-md bg-destructive/15 px-3 py-2 text-xs text-destructive-foreground">{error.message}</p>}
      <div className="mt-4 flex gap-2">
        <button disabled={submitting} className="rounded-md bg-primary-gradient px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">Create shop</button>
        <button type="button" onClick={onCancel} className="rounded-md border border-border bg-surface px-4 py-2 text-sm">Cancel</button>
      </div>
    </form>
  );
}

const inputCls = "w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary";
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>{children}</label>;
}
